import type { Notification } from "./types";
import rest, { api, specUrl } from "./api";
import type { RequestBody, Result } from "./spec";

// Request shapes are spec-derived.
export type NotificationFilter = RequestBody<"notification/list", "post">;
export type MarkAsSeenFilter = RequestBody<"notification/update", "post">;
export type MuteRoomReq = RequestBody<"notification/mute", "post">;
export type UnmuteRoomReq = RequestBody<"notification/unmute", "post">;
export type CreateDeviceTokenReq = RequestBody<"notification/token", "post">;
export type RoomMuteFilter = RequestBody<"notification/mutes", "post">;
export type CreateNotificationReq = RequestBody<"notification/create", "post">;

export type RoomMute = NonNullable<
  Result<"notification/mutes", "post">["items"]
>[number];

/**
 * `notification/list` is annotated `@Success ... result=[]domain.Notification`,
 * but the handler returns `{"items": ..., "size": cnt}`. Typed by hand until the
 * backend annotation is corrected; the URL is still spec-checked.
 */
export interface NotificationListRes {
  items: Notification[];
  size?: number;
}

const notifService = {
  listNotifications: (body: NotificationFilter) =>
    rest.post<NotificationListRes>(specUrl("notification/list"), {
      body,
      hasAuth: true,
    }),
  /** @deprecated use listNotifications */
  getList: (body: NotificationFilter) =>
    rest.post<NotificationListRes>(specUrl("notification/list"), {
      body,
      hasAuth: true,
    }),
  getNotification: (id: number) =>
    api.get("notification/{id}", { path: { id }, hasAuth: true }),
  markAsSeen: (body: MarkAsSeenFilter) =>
    api.post("notification/update", { body, hasAuth: true }),
  /** @deprecated use markAsSeen */
  updateNotificiation: (body: MarkAsSeenFilter) =>
    api.post("notification/update", { body, hasAuth: true }),
  createNotification: (body: CreateNotificationReq) =>
    api.post("notification/create", { body, hasAuth: true }),
  registerDeviceToken: (body: CreateDeviceTokenReq) =>
    api.post("notification/token", { body, hasAuth: true }),
  /** @deprecated use registerDeviceToken */
  createDeviceToken: (body: CreateDeviceTokenReq) =>
    api.post("notification/token", { body, hasAuth: true }),
  muteRoom: (body: MuteRoomReq) =>
    api.post("notification/mute", { body, hasAuth: true }),
  unmuteRoom: (body: UnmuteRoomReq) =>
    api.post("notification/unmute", { body, hasAuth: true }),
  listMutedRooms: (body?: RoomMuteFilter) =>
    api.post("notification/mutes", { body: body ?? {}, hasAuth: true }),
};

export default notifService;

/** @deprecated use Notification */
export type INotification = Notification
/** @deprecated use NotificationFilter */
export type GetNotifications = NotificationFilter
export enum NotificationType {
  INFO                    = 0,
  ROOM_INVITATION         = 1,
  FRIEND_REQUEST_SENT     = 2,
  FRIEND_REQUEST_ACCEPTED = 3,
  ROOM_JOIN_REQUEST       = 4,
  ROOM_REQUEST_APPROVED   = 5,
  ROOM_REQUEST_DENIED     = 6,
  ROOM_MEMBER_REMOVED     = 7,
  MATCH_FOUND             = 8,
  MATCH_EXPIRED           = 9,
  /** A waiting Batch was revealed: "You're in «name»", `sRoomId` is the Knot. */
  KNOT_WAITING_REVEALED   = 10,
}
